import { useEffect, useState } from "react";

import Icon from "@/components/Icon";
import TextField from "@/components/TextField";
import {
  CAREER_INTENT_OPTIONS,
  CLARITY_OPTIONS,
  FACILITY_TYPE_OPTIONS,
  getOtherPositions,
  getRoleCode,
  getRoleLabel,
  getRoleOptionLabel,
  getSuggestedPositions,
  IMPROVEMENT_AREA_OPTIONS,
  MOTIVATION_OPTIONS,
  RETIREMENT_TYPE_OPTIONS,
  TIMEFRAME_OPTIONS,
  type CareerIntentOption
} from "./careerIntentCatalog";
import {
  moveRankedSelection,
  RANKED_POSITION_HINT,
  RANKED_POSITION_SUGGESTED_HINT,
  RANKED_SELECTION_HINT,
  rankedSelectionIndex,
  toggleCareerIntent,
  toggleRankedSelection,
  type CareerIntentFormValues,
  type CareerIntentStepId,
  type RetirementType,
  type Timeframe
} from "./careerIntentForm";
import CareerGoalsRankedSelection from "./_CareerGoalsRankedSelection";
import CareerIntentStatePicker from "./_CareerIntentStatePicker";
import CareerIntentSummarySection from "./_CareerIntentSummarySection";

export default function CareerGoalsQuestionField({
  currentRole,
  disabled = false,
  errorMessage,
  onChange,
  step,
  values
}: {
  currentRole?: string | null;
  disabled?: boolean;
  errorMessage?: string;
  onChange: (values: CareerIntentFormValues) => void;
  step: CareerIntentStepId;
  values: CareerIntentFormValues;
}) {
  switch (step) {
    case "intents":
      return (
        <div className="flex flex-col gap-3">
          <p className="text-body-large text-sys-brand-on-surface">Select all that apply.</p>
          <ErrorMessage message={errorMessage} />
          <div className="flex flex-col gap-2">
            {CAREER_INTENT_OPTIONS.map((option) => {
              const selected = values.selectedIntents.includes(option.value);

              return (
                <OptionCard
                  disabled={disabled}
                  hint={option.hint}
                  key={option.value}
                  label={option.label}
                  onPress={() => onChange(toggleCareerIntent(values, option.value))}
                  selected={selected}
                  testID={`career-intent-intent-${option.value}`}
                />
              );
            })}
          </div>
        </div>
      );

    case "improvement_areas":
      return (
        <div className="flex flex-col gap-3">
          <ErrorMessage message={errorMessage} />
          <OptionList
            disabled={disabled}
            maxSelections={3}
            onChange={(improvementAreas) => onChange({ ...values, improvementAreas })}
            options={IMPROVEMENT_AREA_OPTIONS}
            selected={values.improvementAreas}
            testIDPrefix="career-intent-improvement-area"
          />
        </div>
      );

    case "target_positions":
      return (
        <TargetPositionField
          currentRole={currentRole}
          disabled={disabled}
          errorMessage={errorMessage}
          onChange={onChange}
          values={values}
        />
      );

    case "locations":
      return (
        <div className="flex flex-col gap-3">
          <ErrorMessage message={errorMessage} />
          <CareerIntentStatePicker
            disabled={disabled}
            onChange={(locations) => onChange({ ...values, locations })}
            value={values.locations}
          />
        </div>
      );

    case "facility_types":
      return (
        <div className="flex flex-col gap-3">
          <ErrorMessage message={errorMessage} />
          <OptionList
            disabled={disabled}
            maxSelections={3}
            onChange={(facilityTypes) => onChange({ ...values, facilityTypes })}
            options={FACILITY_TYPE_OPTIONS}
            selected={values.facilityTypes}
            testIDPrefix="career-intent-facility-type"
          />
        </div>
      );

    case "retirement_type":
      return (
        <div className="flex flex-col gap-3">
          <ErrorMessage message={errorMessage} />
          <div className="flex flex-col gap-2" role="radiogroup" aria-label="Retirement type">
            {RETIREMENT_TYPE_OPTIONS.map((option) => (
              <OptionCard
                disabled={disabled}
                hint={option.hint}
                key={option.value}
                label={option.label}
                onPress={() => onChange({ ...values, retirementType: option.value as RetirementType })}
                role="radio"
                selected={values.retirementType === option.value}
                testID={`career-intent-retirement-${option.value}`}
              />
            ))}
          </div>
        </div>
      );

    case "motivations":
      return (
        <div className="flex flex-col gap-3">
          <ErrorMessage message={errorMessage} />
          <OptionList
            disabled={disabled}
            maxSelections={3}
            onChange={(motivations) => onChange({ ...values, motivations })}
            options={MOTIVATION_OPTIONS}
            selected={values.motivations}
            testIDPrefix="career-intent-motivation"
          />
        </div>
      );

    case "timing":
      return (
        <div className="flex flex-col gap-3">
          <p className="text-title-medium text-sys-brand-on-surface">When do you hope this happens?</p>
          <ErrorMessage message={errorMessage} />
          <div className="flex flex-col gap-2" role="radiogroup" aria-label="When do you hope this happens?">
            {TIMEFRAME_OPTIONS.map((option) => (
              <OptionCard
                disabled={disabled}
                key={option.value}
                label={option.label}
                onPress={() => onChange({ ...values, timeframe: option.value as Timeframe })}
                role="radio"
                selected={values.timeframe === option.value}
                testID={`career-intent-timeframe-${option.value}`}
              />
            ))}
          </div>
          <p className="pt-1 text-title-medium text-sys-brand-on-surface">How clear are you on your next steps?</p>
          <div className="flex flex-col gap-2" role="radiogroup" aria-label="How clear are you on your next steps?">
            {CLARITY_OPTIONS.map((option) => (
              <OptionCard
                disabled={disabled}
                key={option.value}
                label={option.label}
                onPress={() => onChange({ ...values, clarity: Number(option.value) })}
                role="radio"
                selected={values.clarity === Number(option.value)}
                testID={`career-intent-clarity-${option.value}`}
              />
            ))}
          </div>
        </div>
      );

    case "notes":
      return (
        <div className="flex flex-col gap-4">
          <div data-testid="career-intent-additional-context">
            <TextField
              disabled={disabled}
              label="Additional context"
              onChangeText={(additionalContext) => onChange({ ...values, additionalContext })}
              placeholder="e.g. dream clubs, non-negotiables, other constraints, etc"
              rows={4}
              type="textarea"
              value={values.additionalContext}
            />
          </div>
          <CareerIntentSummarySection includeNotes={false} values={values} />
        </div>
      );
    default: {
      const _exhaustive: never = step;
      return _exhaustive;
    }
  }
}

function OptionCard({
  disabled = false,
  hint,
  label,
  onPress,
  rank,
  role = "checkbox",
  selected,
  testID
}: {
  disabled?: boolean;
  hint?: string;
  label: string;
  onPress: () => void;
  rank?: number;
  role?: "checkbox" | "radio";
  selected: boolean;
  testID: string;
}) {
  return (
    <button
      type="button"
      aria-checked={selected}
      aria-label={rank != null ? `${rank}. ${label}` : label}
      className={`w-full rounded-lg border px-4 py-3 text-left ${
        selected ? "border-sys-brand-primary bg-sys-brand-primary/5" : "border-sys-brand-outline bg-sys-brand-surface"
      }${disabled ? " opacity-50" : ""}`}
      data-testid={testID}
      disabled={disabled}
      onClick={onPress}
      role={role}
    >
      <span className={`flex ${hint ? "items-start" : "items-center"}`}>
        <span
          className={`mr-3 w-6 text-center text-title-medium ${
            selected ? "text-sys-brand-primary" : "text-sys-brand-on-surface-variant"
          }`}
        >
          {rank != null ? (
            rank
          ) : role === "radio" ? (
            <span
              aria-hidden
              className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${
                selected ? "border-sys-brand-primary" : "border-sys-brand-outline"
              }`}
            >
              {selected ? <span className="h-2.5 w-2.5 rounded-full bg-sys-brand-primary" /> : null}
            </span>
          ) : selected ? (
            "✓"
          ) : (
            "○"
          )}
        </span>
        <span className="flex-1">
          <span className="block text-title-medium text-sys-brand-on-surface">{label}</span>
          {hint ? <span className="mt-1 block text-body-small text-sys-brand-on-surface-variant">{hint}</span> : null}
        </span>
      </span>
    </button>
  );
}

function OptionChip({
  disabled = false,
  label,
  onPress,
  rank,
  selected,
  testID
}: {
  disabled?: boolean;
  label: string;
  onPress: () => void;
  rank?: number;
  selected: boolean;
  testID: string;
}) {
  return (
    <button
      type="button"
      aria-checked={selected}
      aria-label={rank != null ? `${rank}. ${label}` : label}
      className={`rounded-full border px-4 py-3 ${
        selected
          ? "border-sys-brand-outline bg-extended-pgasilver-brand-color text-title-medium-bold text-extended-pgasilver-brand-on-color"
          : "border-sys-brand-outline bg-sys-brand-surface text-body-large text-sys-brand-on-surface"
      }${disabled ? " opacity-50" : ""}`}
      data-testid={testID}
      disabled={disabled}
      onClick={onPress}
      role="checkbox"
    >
      {rank != null ? `${rank}. ${label}` : label}
    </button>
  );
}

function OptionList({
  disabled = false,
  maxSelections,
  onChange,
  options,
  selected,
  testIDPrefix
}: {
  disabled?: boolean;
  maxSelections?: number;
  onChange: (value: string[]) => void;
  options: CareerIntentOption[];
  selected: string[];
  testIDPrefix: string;
}) {
  const atMaxSelections = maxSelections != null && selected.length >= maxSelections;
  const rankedItems = selected.flatMap((value) => {
    const option = options.find((item) => item.value === value);
    return option ? [{ value, label: option.label }] : [];
  });

  return (
    <div className="flex flex-col gap-3">
      {maxSelections ? (
        <p className="text-body-small text-sys-brand-on-surface-variant">{RANKED_SELECTION_HINT}</p>
      ) : null}
      <div className="flex flex-row flex-wrap gap-2">
        {options.map((option) => {
          const isSelected = selected.includes(option.value);

          return (
            <OptionChip
              disabled={Boolean(disabled || (atMaxSelections && !isSelected))}
              key={option.value}
              label={option.label}
              onPress={() =>
                onChange(toggleRankedSelection(selected, option.value, maxSelections ?? Number.MAX_SAFE_INTEGER))
              }
              rank={rankedSelectionIndex(selected, option.value)}
              selected={isSelected}
              testID={`${testIDPrefix}-${option.value}`}
            />
          );
        })}
      </div>
      <CareerGoalsRankedSelection
        disabled={disabled}
        items={rankedItems}
        onMove={(index, direction) => onChange(moveRankedSelection(selected, index, direction))}
        testIDPrefix={testIDPrefix}
      />
    </div>
  );
}

function ErrorMessage({ message }: { message?: string }) {
  if (!message) return null;

  return (
    <p aria-live="polite" className="text-body-medium text-sys-brand-error" data-testid="career-intent-question-error">
      {message}
    </p>
  );
}

function TargetPositionField({
  currentRole,
  disabled = false,
  errorMessage,
  onChange,
  values
}: {
  currentRole?: string | null;
  disabled?: boolean;
  errorMessage?: string;
  onChange: (values: CareerIntentFormValues) => void;
  values: CareerIntentFormValues;
}) {
  const [showOtherPositions, setShowOtherPositions] = useState(false);
  const currentRoleCode = getRoleCode(currentRole);
  const hasCurrentRole = Boolean(currentRoleCode);
  const suggestedPositions = hasCurrentRole ? getSuggestedPositions(currentRoleCode) : [];
  const otherPositions = getOtherPositions(currentRoleCode);

  useEffect(() => {
    if (!hasCurrentRole) return;
    if (values.targetPositions.some((position) => otherPositions.some((option) => option.value === position))) {
      setShowOtherPositions(true);
    }
  }, [hasCurrentRole, otherPositions, values.targetPositions]);

  return (
    <div className="flex flex-col gap-3">
      <ErrorMessage message={errorMessage} />
      <p className="text-body-small text-sys-brand-on-surface-variant">
        {hasCurrentRole && suggestedPositions.length > 0 ? RANKED_POSITION_SUGGESTED_HINT : RANKED_POSITION_HINT}
      </p>
      {hasCurrentRole && suggestedPositions.length > 0 ? (
        <div className="flex flex-col gap-2">
          {suggestedPositions.map((option) => (
            <PositionOption
              disabled={disabled}
              key={option.value}
              onChange={(targetPositions) => onChange({ ...values, targetPositions })}
              option={option}
              selected={values.targetPositions}
            />
          ))}
        </div>
      ) : null}
      {hasCurrentRole ? (
        <>
          <button
            type="button"
            aria-expanded={showOtherPositions}
            className="flex items-center gap-1 self-start text-title-medium text-sys-brand-primary"
            data-testid="career-intent-toggle-other-positions"
            disabled={disabled}
            onClick={() => setShowOtherPositions((current) => !current)}
          >
            Choose a different position
            <Icon
              name={showOtherPositions ? "expand_less" : "expand_more"}
              size={20}
              className="text-sys-brand-primary"
            />
          </button>
          {showOtherPositions ? (
            <div className="flex flex-col gap-2">
              {otherPositions.map((option) => (
                <PositionOption
                  disabled={disabled}
                  key={option.value}
                  onChange={(targetPositions) => onChange({ ...values, targetPositions })}
                  option={option}
                  selected={values.targetPositions}
                />
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <div className="flex flex-col gap-2">
          {otherPositions.map((option) => (
            <PositionOption
              disabled={disabled}
              key={option.value}
              onChange={(targetPositions) => onChange({ ...values, targetPositions })}
              option={option}
              selected={values.targetPositions}
            />
          ))}
        </div>
      )}
      <CareerGoalsRankedSelection
        disabled={disabled}
        items={values.targetPositions.map((value) => ({
          value,
          label: getRoleLabel(value) ?? value
        }))}
        onMove={(index, direction) =>
          onChange({ ...values, targetPositions: moveRankedSelection(values.targetPositions, index, direction) })
        }
        testIDPrefix="career-intent-target-position"
      />
    </div>
  );
}

function PositionOption({
  disabled = false,
  onChange,
  option,
  selected
}: {
  disabled?: boolean;
  onChange: (value: string[]) => void;
  option: { value: string; label: string };
  selected: string[];
}) {
  const isSelected = selected.includes(option.value);
  const optionDisabled = disabled || (!isSelected && selected.length >= 3);

  return (
    <OptionCard
      disabled={optionDisabled}
      label={getRoleOptionLabel(option)}
      onPress={() => onChange(toggleRankedSelection(selected, option.value))}
      rank={rankedSelectionIndex(selected, option.value)}
      selected={isSelected}
      testID={`career-intent-target-position-${option.value.toLowerCase()}`}
    />
  );
}
